import axios from "axios";
//---- Dependencies ----//
import "./interceptors";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Router, Route, browserHistory, IndexRedirect } from "react-router";
import { I18nextProvider } from "react-i18next";
import { SnackbarProvider } from "notistack";
import ReactGA from 'react-ga';
import * as Sentry from "@sentry/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { withSuspense } from "./Components/Common/hoc/withSuspense";
import { withCompatTheme } from "./Components/Menu/Settings/hoc/withCompatTheme";

import i18n from "./i18n.jsx";
import { store } from "./Store";

//---- Actions ----//
import { SetLocales, SetUserAndConnection, SetUserPermissions } from "./Actions/User";

//---- Commons ----//
import CloseSnackbar from "./Components/Common/CloseSnackbar";
import GetCookie from "./Components/Common/Functions/GetCookie";

//---- CSS ----//
import "./Icons/styles.css";
import "./NewIcons/styles.css";
import "./Style/Global.css";
import "./Components/Menu/MaterialTripList/MailVisualEditor/css/index.css";
import 'react-virtualized/styles.css';

//---- Components ----//
import { LoadingBackDrop } from "./Components/Common/LoadingBackdrop";
import SetCookie from "./Components/Common/Functions/SetCookie";
import { ToggleAccommodationRecommended } from "./Actions/Accommodation";
import { SetCurrencyList } from "./Actions/Base";
import CheckBeforeRequest from "./Components/Common/CheckBeforeRequest";
import Notify from "./Components/Common/Notify";
import { ItinerarySavingRouteBlock } from "./Components/Itinerary/itinerarySavingRouteBlock";
import { theme } from "./theme";
import {
    setSeeProductsMargins
} from "./Components/CartMaterial/redux/cartConstructionReducer";

const identifier = window.location.host.replace("www.", "").split('.')[0];

//---- NotFound ----//
const NotFound = withSuspense(
    React.lazy(() =>
        import("./Components/NotFound/NotFound.jsx")
    )
);

const TokenExpiryWarning = withSuspense(
    React.lazy(() =>
        import("./Components/Header/TokenExpiryWarning")
    )
);

const PasswordExpiryDialog = withSuspense(
    React.lazy(() =>
        import("./Components/Common/PasswordExpiryDialog")
    )
);

//---- Base -> Header - Component ----//
const Base = withSuspense(
    React.lazy(() =>
        import("./Components/Base/Base.jsx")
    )
);
const CrmRedirection = withSuspense(
    React.lazy(() =>
        import("./Components/Base/crmRedirection")
    )
);

//---- Itinerary ----//
const LazyItinerary = withSuspense(
    React.lazy(() =>
        import("./Components/Itinerary/Itinerary.tsx")
    )
);
// the route leave hook is mounted outside the lazy chunk: while it loads, the header stays clickable
// and a pending duplication recalc must already block leaving the page
function Itinerary(props) {
    return (
        <>
            <LazyItinerary {...props} />
            <ItinerarySavingRouteBlock route={props.route} />
        </>
    );
}

//---- PassengerGroup ----//
const FlightGroups = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightGroups/FlightGroups.js")
    )
);

//---- FlightSearch ----//
const FlightSearch = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightSearch/FlightSearch.js")
    )
);

//---- SelectedFlight ----//
const SelectedFlight = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightSelected/FlightSelected.js")
    )
);

//---- Accommodation ----//
const AccommodationList = withSuspense(
    React.lazy(() =>
        import("./Components/Accommodation/AccommodationList")
    )
);
const MaterialAccommodationCard = withSuspense(
    React.lazy(() =>
        import("./Components/Accommodation/MaterialAccommodationCard.jsx")
    )
);

//---- Poi ----//
const PoiList = withSuspense(
    React.lazy(() =>
        import("./Components/PoiMaterial/PoiList.js")
    )
);
const PoiCard = withSuspense(
    React.lazy(() =>
        import("./Components/PoiMaterial/PoiCard.js")
    )
);

//---- Cars ----//
const CarsSearch = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsSearch.jsx")
    )
);
//---- MaterialCars ----//
const MaterialCarsSearch = withSuspense(
    React.lazy(() =>
        import("./Components/CarsMaterial/MaterialCarsSearch.js")
    )
);
const CarsSearchOneStep = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsSearchOneStep.jsx")
    )
);
const CarsStep = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsStep.jsx")
    )
);
const CarsResults = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsResults.jsx")
    )
);
const CarsAddToCart = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsAddToCart.jsx")
    )
);

//---- Cart ----//
const Cart = withSuspense(
    React.lazy(() =>
        import("./Components/CartMaterial/Cart.js")
    )
);
const CartQuotation = withSuspense(
    React.lazy(() => {
        import("./Components/CartMaterial/CartQuotation.js");
    })
);
//---- Booking ----//
const Booking = withSuspense(
    React.lazy(() =>
        import("./Components/BookingMaterial/Booking.js")
    )
);
//---- Lockable Fare (Pose d'option) recap ----//
const LockableFareOption = withSuspense(
    React.lazy(() =>
        import("./Components/CartMaterial/lockableFare/LockableFareOption")
    )
);
//---- Summary-Quotation ----//
const SummaryQuotation = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryQuotation.js")
    )
);
const SummaryPayment = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPayment.js")
    )
);
const SummaryPaymentSuccess = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPaymentSuccess.js")
    )
);
const SummaryPaymentError = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPaymentError.js")
    )
);
const SummaryDocuments = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryDocuments.js")
    )
);
const SummaryInscription = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryInscription.js")
    )
);
const SummaryContact = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryContact.js")
    )
);

//---- Provider-Website ----//
const ProviderWebsite = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderWebsite/MaterialProviderWebsite.js")
    )
);
const ProviderQuotation = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderQuotation/ProviderQuotation.tsx")
    )
);
const ProviderBooking = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderBooking/ProviderBooking.tsx")
    )
);
//---- PCancel-Website ----//
const ProviderCancel = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderWebsite/ProviderCancel.jsx")
    )
);

//---- Home ----//
const Home = withSuspense(
    React.lazy(() =>
        import("./Components/Home/MaterialHome.js")
    )
);
//---- Home visiteurs ----//
const VisiteursHome = withSuspense(
    React.lazy(() =>
        import("./Components/VisiteursHome/VisiteursHome.js")
    )
);
//---- ItineraryType & others products ----//
const DestinationProducts = withSuspense(
    React.lazy(() =>
        import("./Components/ItineraryType/MaterialDestinationProducts.js")
    )
);
const VisiteursDestinationProducts = withSuspense(
    React.lazy(() =>
        import("./Components/VisiteursHome/VisiteursDestinationProducts.js")
    )
);
//---- ItineraryTypeDetails & others products ----//
const ItineraryTypeDetails = withSuspense(
    React.lazy(() =>
        import("./Components/ItineraryType/ItiTypes/ItiTypeDetails.js")
    )
);
const PackagesDetails = withSuspense(
    React.lazy(() =>
        import("./Components/ItineraryType/PackagesMaterial/PackagesDetails.js")
    )
);
const CircuitsDetails = withSuspense(
    React.lazy(() =>
        import("./Components/ItineraryType/CircuitsMaterial/CircuitsDetails.js")
    )
);

//---- Menu ----//
//---- BaseMenu ----//
const BaseMenu = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/BaseMenu/MaterialBaseMenu.js")
    )
);
//---- Profile ----//
const Profile = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Profile/MaterialProfile.js")
    )
);

//---- Agency Profile ----//
const AgencyProfile = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyProfile/MaterialAgencyProfile.js")
    )
);

const TripList = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/MaterialTripList/TripList")
    )
);

const TripListInfo = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/MaterialTripList/TripListInfo")
    )
);

//---- AgencyMarging ----//
const AgencyMargin = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyMargin/MarginList.js")
    )
);

//---- AgencyUserManagement ----//
const AgencyUserManagement = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyUserManagement/AgencyUserTable.js")
    )
);

//---- AgencyCustomerManagement ----//
const AgencyCustomerManagement = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyCustomerManagement/AgencyCustomerManagement.jsx")
    )
);

//---- Help ----//
const Help = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Help/TripListVideo.jsx")
    )
);

//---- Settings ----//
const Settings = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/Settings.jsx")
    )
);

//---- Transfers ----//
const Transfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/Transfers.tsx")
    )
);
const StepTransfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/StepTransfers.jsx")
    )
);
const CityTransfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/CityTransfers.jsx")
    )
);

const AgencyOfflineMargin = withSuspense(
    React.lazy(() =>
        import("./Components/AgencyOfflineMargin/AgencyOfflineMargin")
    )
);
// ---- Train ----//
const TrainSearch = withSuspense(React.lazy(() => import('./Components/train/trainSearch')));
const TrainListRoot = withSuspense(React.lazy(() => import('./Components/train/trainList')));

const CircuitCartSteps = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartSteps")
    )
);

const CircuitCartStepsBooking = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsBooking")
    )
);

const CircuitCartStepsBookingCartRecap = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsBookingCartRecap")
    )
);

const CircuitCartStepsTravelers = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsTravelers")
    )
);

const requireAuth = (router, replace, callback) => {
    let token = GetCookie("token");
    if ((token === undefined || token === null || token === "") && !(['visiteurs'].includes(identifier))) {
        replace({
            pathname: `/home`,
            state: { nextPathname: router.location.pathname }
        });
        callback();
    }
    callback();
};

const checkDepartureReturn = (router, replace, callback, store) => {
    if (store.getState().itinerary.departure_destination.city === null && store.getState().itinerary.return_destination.city === null) {
        replace({
            pathname: `/${router.params.url_name}/apps/itinerary`,
            state: { nextPathname: router.location.pathname }
        });
    }
    callback();
};

const checkFlight = (router, replace, callback, store) => {
    if (store.getState().flight.index_group === null || store.getState().flight.copy_index_group === null) {
        replace({
            pathname: `/${router.params.url_name}/apps/flight-groups`,
            state: { nextPathname: router.location.pathname }
        });
    }
    callback();
};

const getInstance = (url_name) => {
    let domain_name = window.location.host.replace("www.", "");
    let customer_identifier = "";
    if (url_name !== undefined && url_name !== null) {
        customer_identifier = url_name;
    } else {
        customer_identifier = domain_name.split('.')[0];
    }
    if (window.location.host.includes("localhost")) {
        domain_name = "dev.facilitatrip.com";
        if (url_name === undefined || url_name === null) {
            customer_identifier = "dev";
        }
    } else if (window.location.host === "sistema.volonline.it") {
        domain_name = "volonline.facilitatrip.com";
        customer_identifier = "volonline";
    }
    return axios({
        url: `${API_HREF}instances/?customer_identifier=${customer_identifier}&domain_name=${domain_name}`,
        method: "GET"
    }).then(response => (function (data) {
        console.log(data);
        if (data.favicon !== undefined && data.favicon !== null && data.favicon.thumbnail_little !== undefined && data.favicon.url !== undefined) {
            let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = (data.favicon.thumbnail_little !== null ? data.favicon.thumbnail_little : data.favicon.url);
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        if (data.analytics !== null) {
            ReactGA.initialize(data.analytics);
            ReactGA.pageview(window.location.pathname + window.location.search);
        }
        localStorage.setItem("config", JSON.stringify(data));
        if (data.quotation_code === "amerigo" && !store.getState().accommodation.recommended) {
            store.dispatch(ToggleAccommodationRecommended());
        }
        store.dispatch({ type: "USER_SET_ACTIVATED_LOCALES", payload: data.activated_locales });
        window.id_owner = data.instance_id;
        window.id_buyer = data.client_id;
        window.url_name = customer_identifier;
    })(response.data)).catch(function (error) {
        console.log(error);
        if (error !== undefined && error !== null && error.status !== undefined && error.status !== null && error.status === 404) {
            Notify(/*i18n.t("notify.wrong_instance")*/ "L'instance que vous essayez d'accéder n'existe pas, veuillez vérifier votre url d'accès", { status: "danger", pos: "top-center" });
        }
    });
};

const getLocales = (store) => {
    const { pass_check, headers } = CheckBeforeRequest();
    return axios({
        url: `${API_HREF}locales/`,
        method: "GET",
        headers: headers
    }).then(response => (function (data) {
        const default_locale = data.results.find((el) => {
            return el.language_code === 'fr';
        });
        const main_locale = data.results.find((el) => {
            return el.language_code === i18n.language;
        });
        store.dispatch(SetLocales(data.results));
        store.dispatch({ type: "USER_SET_MAIN_LOCALE", payload: main_locale ? main_locale : default_locale });
    })(response.data)).catch(function (error) {
        console.log(error);
    });
};

const getCurrencies = (store) => {
    const { pass_check, headers } = CheckBeforeRequest();
    if (store.getState().base.currency_list.length === 0) {
        return axios({
            url: `${API_HREF}currencies/?limit=200`,
            method: "GET",
            headers: headers
        }).then(response => (function (data) {
            if (data.results.length > 0) {
                store.dispatch(SetCurrencyList(data.results));
            }
        })(response.data)).catch(function (error) {
            console.log(error);
        });
    }
};

const loadProfile = (store, router) => {
    let token = GetCookie("token");
    let id_user = GetCookie("id_user");
    let client_user = GetCookie("client_user");
    let domain_name = window.location.host.replace("www.", "");
    if (store.getState().user.user === null && token !== undefined && token !== null && token !== "") {
        return axios({
            url: `${API_HREF}client/${client_user}/user/${id_user}/`,
            method: "GET",

            headers: {
                "Authorization": "Token " + token,
                "Accept": "application/json",
                "Content-Type": "application/json"
            }
        }).then(response => (function (data) {
            console.log(data);
            if (JSON.parse(localStorage.getItem("config")).quotation_code === "amerigo" && !data.is_superuser) {
                Notify("FacilitaTrip est forcée d'interrompre le service Free Independent Traveler pour le compte du tour-opérateur Amerigo. Nous en sommes sincèrement désolés, mais nous poursuivons l'aventure avec d'autres partenaires", { status: "danger", pos: "top-center" });
            } else if (JSON.parse(localStorage.getItem("config")).quotation_code === "pbe" && !data.is_superuser) {
                Notify("FacilitaTrip est forcée d'interrompre le service Free Independent Traveler pour le compte du tour-opérateur Plus Belle l'Europe. Nous en sommes sincèrement désolés, mais nous poursuivons l'aventure avec d'autres partenaires", { status: "danger", pos: "top-center" });
            } else {
                store.dispatch(SetUserAndConnection(data, true));
                if (data.is_superuser === false) {
                    axios({
                        url: `${API_HREF}client/${client_user}/user/${id_user}/permissions/?all=true`,
                        method: "GET",

                        headers: {
                            "Authorization": "Token " + token,
                            "Accept": "application/json",
                            "Content-Type": "application/json"
                        }
                    }).then(response => (function (data_perm) {
                        console.log('data_perm:', data_perm);
                        store.dispatch(SetUserPermissions(data_perm));
                    })(response.data)).catch(function (error) {
                        console.log(error);
                    });
                } else {
                    axios({
                        url: `${API_HREF}permissions/?all=true`,
                        method: "GET",

                        headers: {
                            "Authorization": "Token " + token,
                            "Accept": "application/json",
                            "Content-Type": "application/json"
                        }
                    }).then(response => (function (data_perm) {
                        console.log('data_perm:', data_perm);
                        store.dispatch(SetUserPermissions(data_perm));
                    })(response.data)).catch(function (error) {
                        console.log(error);
                    });
                }
                if (client_user !== undefined && client_user !== null && client_user !== "" && router.location.pathname === "/home") {
                    if (window.location.host.includes("localhost")) {
                        window.location = `/home/dev`;
                    } else if (data.client_full.type !== 2) {
                        window.location = `/home/${domain_name.split('.')[0]}`;
                    } else {
                        window.location = `/home/${client_user}`;
                    }
                }
            }
        })(response.data)).catch(function (error) {
            console.log(error);
        });
    } else if (client_user !== undefined && client_user !== null && client_user !== "" && router.location.pathname === "/home") {
        if (window.location.host.includes("localhost")) {
            window.location = `/home/dev`;
        } else if (store.getState().user.user?.client_full?.type !== 2) {
            window.location = `/home/${domain_name.split('.')[0]}`;
        } else {
            window.location = `/home/${client_user}`;
        }
    }
};

const loadTrip = (router) => {
    let user_token = GetCookie("token");
    let id_user = GetCookie("id_user");
    let client_user = GetCookie("client_user");
    const crmCookies = {
        crm_token: GetCookie("crm_token"),
        crm_id_user: GetCookie("crm_id_user"),
        crm_client_user: GetCookie("crm_client_user"),
        isFromCrm: GetCookie("isFromCrm")
    };
    //Delete the cookie
    let cookies = document.cookie.split("; ");
    for (let c = 0; c < cookies.length; c++) {
        let d = window.location.hostname.split(".");
        while (d.length > 0) {
            let cookieBase = encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) + "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=" + d.join(".") + " ;path=";
            let p = location.pathname.split("/");
            document.cookie = cookieBase + "/";
            while (p.length > 0) {
                document.cookie = cookieBase + p.join("/");
                p.pop();
            }
            d.shift();
        }
    }
    SetCookie("trip_id", router.params.trip_id, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    SetCookie("trip_id_version", router.params.trip_version, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    SetCookie("trip_token", router.params.trip_token, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    SetCookie("token", user_token, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    SetCookie("id_user", id_user, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    SetCookie("client_user", client_user, 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
    if (crmCookies.crm_token) {
        for (const key in crmCookies) {
            SetCookie(key, crmCookies[key], 365, `/`, (window.location.host.includes("localhost") ? null : window.location.host));
        }
    }

    if (router.location.query.redirect === 'cart') {
        window.location = `/${router.params.url_name}/apps/cart`;
    } else {
        window.location = `/${router.params.url_name}/apps/itinerary`;
    }
};

if (
    window.location.hostname.split('.')[2] === 'com' &&
    window.location.hostname !== 'demo.facilitatrip.com' &&
    !window.location.host.includes('localhost')
) {
    Sentry.init({
        dsn: "https://6416f007833e40b8ad27b4947cb330e0@o161232.ingest.sentry.io/1227144",
        integrations: [
            Sentry.browserTracingIntegration()
        ],

        //We recommend adjusting this value in production, or using tracesSampler
        //for finer control
        tracesSampleRate: 1.0
    });
}

await getInstance();

ReactDOM.render(
    <Provider store={store}>
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
                <CssBaseline>
                    <SnackbarProvider anchorOrigin={{ vertical: "top", horizontal: "right" }} disableWindowBlurListener={true} action={key => CloseSnackbar(key)}>
                        <TokenExpiryWarning />
                        <PasswordExpiryDialog />
                        <Router history={browserHistory}>
                            <Route path={"access/:url_name/:trip_id/:trip_version/:trip_token"} onEnter={(router) => {
                                loadTrip(router);
                            }} />
                            <Route path={"agency-price-edit/:trip_id/:trip_version/:trip_token/:token"} component={AgencyOfflineMargin} onEnter={(router) => {
                                getCurrencies(store);
                                getLocales(store);
                            }} />
                            <Route path={"home(/:url_name)"} component={identifier !== 'visiteurs' ? Home : VisiteursHome} onEnter={async (router, replace, callback) => {
                                try {
                                    await loadProfile(store, router);
                                    await getCurrencies(store);
                                    await getLocales(store);
                                } finally {
                                    callback();
                                }
                            }} />
                            <Route path={"/:url_name"} onEnter={async (router, replace, callback) => {
                                const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
                                const host = window.location.host;
                                const domainPart = host.includes("localhost") ? "" : `;Domain=${host}`;
                                document.cookie = `token=;${expires};path=/${router.params.url_name}${domainPart}`;
                                try {
                                    await loadProfile(store, router);
                                    await getCurrencies(store);
                                    await getLocales(store);
                                } finally {
                                    callback();
                                }
                            }}>
                                <Route path="crm-redirection" component={CrmRedirection} />
                                <Route path={"reset-password"} component={identifier !== 'visiteurs' ? Home : VisiteursHome} />
                                <Route path={"destination-product/:desti_id"} component={identifier !== 'visiteurs' ? DestinationProducts : VisiteursDestinationProducts} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }} />
                                <Route path={"packages-details/:itinerary_type_name/:itinerary_type_id"} component={PackagesDetails} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }} />
                                <Route path={"circuits-details/:itinerary_type_name/:itinerary_type_id"} component={CircuitsDetails} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }} />
                                <Route path={"itinerary-type-details/:itinerary_type_name/:itinerary_type_id"} component={ItineraryTypeDetails} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }} />
                                <Route path={"summary-quotation/payment/:trip_id/:trip_id_version/:trip_token"} component={SummaryPayment} show_map_calendar={false} />
                                <Route path={"summary-quotation/:trip_id/:trip_id_version/:trip_token"} component={SummaryQuotation} show_map_calendar={false} />
                                <Route path={"summary-inscription/:trip_id/:trip_id_version/:trip_token"} component={SummaryInscription} show_map_calendar={false} />
                                <Route path={"summary-documents/:trip_id/:trip_id_version/:trip_token"} component={SummaryDocuments} show_map_calendar={false} />
                                <Route path={"summary-contact/:trip_id/:trip_id_version/:trip_token"} component={SummaryContact} show_map_calendar={false} />
                                <Route path={"summary-quotation/payment/success/:trip_id/:trip_id_version/:trip_token"} component={SummaryPaymentSuccess} show_map_calendar={false} />
                                <Route path={"summary-quotation/payment/error/:trip_id/:trip_id_version/:trip_token"} component={SummaryPaymentError} show_map_calendar={false} />
                                <Route path={"provider-website/:trip_id/:trip_id_version/:trip_token/:product_type/:product_id"} component={ProviderWebsite} show_map_calendar={false} />
                                <Route path={"provider-quotation/provider/:providerId/trip/:tripId/version/:tripVersion/stack/:stackNumber/token/:tripToken/provider-token/:providerToken"} component={ProviderQuotation} show_map_calendar={false} />
                                <Route path={"provider-booking/provider/:providerId/trip/:tripId/version/:tripVersion/token/:tripToken/provider-token/:providerToken"} component={ProviderBooking} show_map_calendar={false} />
                                <Route path={"provider-cancel/:trip_id/:trip_id_version/:trip_token/:product_type/:product_id"} component={ProviderCancel} show_map_calendar={false} />
                                <Route path={"apps"} component={Base} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }}>
                                    <IndexRedirect to={"itinerary"} />
                                    {
                                        //<Route path={ "steps" } component={ AppsSteps } show_map_calendar={ true } type={ 0 }/>
                                    }
                                    <Route path={"itinerary"} component={Itinerary} show_map_calendar={true} type={0} timeline={false} />
                                    <Route path={"flight-groups"} component={withCompatTheme(FlightGroups)} show_map_calendar={false} timeline={false} />
                                    <Route path={"selected-flight"} component={withCompatTheme(SelectedFlight)} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkFlight(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"flight-search/:use_cache"} component={withCompatTheme(FlightSearch)} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkFlight(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"accommodation"} component={withCompatTheme(AccommodationList)} show_map_calendar={true} type={1} timeline={true} />
                                    <Route path={"accommodation-card/:id"} component={withCompatTheme(MaterialAccommodationCard)} type={1} show_map_calendar={false} timeline={false} />
                                    <Route path={"poi"} component={withCompatTheme(PoiList)} show_map_calendar={true} type={2} timeline={true} />
                                    <Route path={"poi-card/:id"} component={withCompatTheme(PoiCard)} show_map_calendar={false} timeline={false} />
                                    <Route path={"cart"} component={Cart} show_map_calendar={false} timeline={false} />
                                    <Route path={"cart-quotation"} component={CartQuotation} show_map_calendar={false} timeline={false} />
                                    <Route path={"booking"} component={Booking} show_map_calendar={false} timeline={false} />
                                    <Route path={"lockable-fare-option"} component={LockableFareOption} show_map_calendar={false} timeline={false} />
                                    <Route path={"cars-search"} component={withCompatTheme(CarsSearch)} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkDepartureReturn(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"material-cars-search"} component={withCompatTheme(MaterialCarsSearch)} timeline={false} show_map_calendar={false} name={""} />
                                    <Route path={"cars-search-one-step"} component={CarsSearchOneStep} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkDepartureReturn(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"cars-step"} component={CarsStep} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkDepartureReturn(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"cars-results/:id_step"} component={CarsResults} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkDepartureReturn(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"cars-add-to-cart/:id_step"} component={CarsAddToCart} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                        checkDepartureReturn(router, replace, callback, store);
                                    }} timeline={false} />
                                    <Route path={"transfers"} component={Transfers} show_map_calendar={false} timeline={false} />
                                    <Route path={"step-transfers"} component={StepTransfers} show_map_calendar={false} timeline={false} />
                                    <Route path={"city-transfers"} component={CityTransfers} show_map_calendar={false} timeline={false} />
                                    <Route path={"train"} show_map_calendar={false} timeline={false} >
                                        <IndexRedirect to={'search'} />
                                        <Route path={"search"} component={TrainSearch} show_map_calendar={false} timeline={false} />
                                        <Route path={"list"} component={TrainListRoot} show_map_calendar={false} timeline={false} />
                                    </Route>
                                    <Route
                                        path="circuit-cart-steps"
                                        component={CircuitCartSteps}
                                        show_map_calendar={false}
                                        timeline={false}
                                        disabledLoader
                                        disabledHeader
                                    >
                                        <Route
                                            path="booking"
                                            component={CircuitCartStepsBooking}
                                        >
                                            <IndexRedirect to={"cart-recap"} />
                                            <Route path="travelers" component={CircuitCartStepsTravelers} />
                                            <Route
                                                path="cart-recap"
                                                component={CircuitCartStepsBookingCartRecap}
                                                onLeave={() => {
                                                    store.dispatch(setSeeProductsMargins(false));
                                                }}
                                            />
                                        </Route>
                                    </Route>
                                </Route>
                                <Route path={"menu"} component={BaseMenu} onEnter={(router, replace, callback) => {
                                    requireAuth(router, replace, callback);
                                }}>
                                    <IndexRedirect to={"profile"} />
                                    <Route path={"profile"} component={Profile} />
                                    <Route path={"agency-profile"} component={AgencyProfile} />
                                    <Route path={"trip-list"} component={TripList} />
                                    <Route path={"trip-info/:trip_id"} component={TripListInfo} />
                                    <Route path={"agency-margin"} component={AgencyMargin} />
                                    <Route path={"agency-user-management"} component={AgencyUserManagement} />
                                    <Route path={"agency-customer-management"} component={AgencyCustomerManagement} />
                                    <Route path={"help"} component={Help} />
                                    <Route path={"settings"} component={Settings} />
                                </Route>
                                {
                                    /*
                            <Route path={ "/not-found" } component={ NotFound }/>
                            <Route path={ "*" } component={ NotFound }/>
                                 */
                                }
                            </Route>
                        </Router>
                        <LoadingBackDrop main />
                    </SnackbarProvider>
                </CssBaseline>
            </ThemeProvider>
        </I18nextProvider>
    </Provider>,
    document.getElementById("app")
);
